import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Autocomplete,
  Alert,
  Snackbar,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Refresh,
  Search,
  Add,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  AutoFixHigh,
} from '@mui/icons-material';
import {
  fetchFAQs,
  createFAQ,
  updateFAQ,
  deleteFAQ,
  regenerateEmbedding,
  bulkRegenerateEmbeddings,
} from '../../../services/faq';

const getCategoryOptions = (t) => [
  { value: 'general', label: t('ai:faq.categories.general') },
  { value: 'registration', label: t('ai:faq.categories.registration') },
  { value: 'search', label: t('ai:faq.categories.search') },
  { value: 'payment', label: t('ai:faq.categories.payment') },
  { value: 'shipping', label: t('ai:faq.categories.shipping') },
  { value: 'orders', label: t('ai:faq.categories.orders') },
  { value: 'account', label: t('ai:faq.categories.account') },
  { value: 'product', label: t('ai:faq.categories.product') },
];

const addSerialNumbers = (data, page, pageSize) =>
  data.map((row, idx) => ({
    ...row,
    sn: page * pageSize + idx + 1,
  }));

const FAQManagementPage = () => {
  const { t } = useTranslation(['ai', 'common', 'sidebar']);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Form state
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'general',
    keywords: [],
    priority: 0,
    is_active: true,
  });

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    is_active: 'all',
  });

  // Debounce hook
  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
  };

  const debouncedSearch = useDebounce(filters.search, 500);

  // Fetch FAQs
  const fetchFAQsList = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        page_size: pageSize,
      };

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      if (filters.category && filters.category !== 'all') {
        params.category = filters.category;
      }

      if (filters.is_active && filters.is_active !== 'all') {
        params.is_active = filters.is_active === 'true';
      }

      const response = await fetchFAQs(params);

      if (response.results) {
        setFaqs(response.results);
        setTotalCount(response.count || response.results.length);
      } else if (Array.isArray(response)) {
        setFaqs(response);
        setTotalCount(response.length);
      } else {
        setFaqs([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
      setFaqs([]);
      setTotalCount(0);
      showSnackbar(t('ai:faq.messages.error'), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, filters.category, filters.is_active]);

  useEffect(() => {
    fetchFAQsList();
  }, [fetchFAQsList]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (faq = null) => {
    if (faq) {
      setEditingFAQ(faq);
      setFormData({
        question: faq.question || '',
        answer: faq.answer || '',
        category: faq.category || 'general',
        keywords: Array.isArray(faq.keywords) ? faq.keywords : [],
        priority: faq.priority || 0,
        is_active: faq.is_active !== undefined ? faq.is_active : true,
      });
    } else {
      setEditingFAQ(null);
      setFormData({
        question: '',
        answer: '',
        category: 'general',
        keywords: [],
        priority: 0,
        is_active: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingFAQ(null);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.question.trim() || !formData.answer.trim()) {
        showSnackbar(t('ai:faq.messages.fillRequired'), 'error');
        return;
      }

      if (editingFAQ) {
        await updateFAQ(editingFAQ.id, formData);
        showSnackbar(t('ai:faq.messages.updated'));
      } else {
        await createFAQ(formData);
        showSnackbar(t('ai:faq.messages.created'));
      }

      handleCloseDialog();
      fetchFAQsList();
    } catch (error) {
      console.error('Failed to save FAQ:', error);
      showSnackbar(
        error.response?.data?.detail || t('ai:faq.messages.error'),
        'error'
      );
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('ai:faq.messages.deleteConfirm'))) {
      return;
    }

    try {
      await deleteFAQ(id);
      showSnackbar(t('ai:faq.messages.deleted'));
      fetchFAQsList();
    } catch (error) {
      console.error('Failed to delete FAQ:', error);
      showSnackbar(t('ai:faq.messages.error'), 'error');
    }
  };

  const handleRegenerateEmbedding = async (id) => {
    try {
      await regenerateEmbedding(id);
      showSnackbar(t('ai:faq.messages.embeddingRegenerated'));
    } catch (error) {
      console.error('Failed to regenerate embedding:', error);
      showSnackbar(t('ai:faq.messages.error'), 'error');
    }
  };

  const handleBulkRegenerate = async () => {
    if (!window.confirm(t('ai:faq.messages.bulkRegenerateConfirm'))) {
      return;
    }

    try {
      const response = await bulkRegenerateEmbeddings();
      showSnackbar(response.message || t('ai:faq.messages.embeddingsRegenerated'));
    } catch (error) {
      console.error('Failed to bulk regenerate embeddings:', error);
      showSnackbar(t('ai:faq.messages.error'), 'error');
    }
  };

  const categoryOptions = getCategoryOptions(t);

  const getCategoryLabel = (category) => {
    const option = categoryOptions.find(opt => opt.value === category);
    return option ? option.label : category;
  };

  const columns = [
    {
      field: 'sn',
      headerName: t('ai:faq.table.sn'),
      width: 60,
      sortable: false,
    },
    {
      field: 'question',
      headerName: t('ai:faq.table.question'),
      width: 300,
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'category',
      headerName: t('ai:faq.table.category'),
      width: 200,
      renderCell: (params) => (
        <Chip
          label={getCategoryLabel(params.value)}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: 'priority',
      headerName: t('ai:faq.table.priority'),
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value > 5 ? 'error' : params.value > 0 ? 'warning' : 'default'}
        />
      ),
    },
    {
      field: 'is_active',
      headerName: t('ai:faq.table.status'),
      width: 100,
      renderCell: (params) => (
        <Chip
          icon={params.value ? <Visibility /> : <VisibilityOff />}
          label={params.value ? t('ai:faq.filters.active') : t('ai:faq.filters.inactive')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'view_count',
      headerName: t('ai:faq.table.views'),
      width: 100,
    },
    {
      field: 'helpful_count',
      headerName: t('ai:faq.table.helpful'),
      width: 100,
    },
    {
      field: 'created_at',
      headerName: t('ai:faq.table.created'),
      width: 150,
      renderCell: (params) => {
        if (!params.value) return '-';
        try {
          const date = new Date(params.value);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          });
        } catch (e) {
          return params.value;
        }
      },
    },
    {
      field: 'actions',
      headerName: t('ai:faq.table.actions'),
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleOpenDialog(params.row)}
            title={t('ai:faq.actions.edit')}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="secondary"
            onClick={() => handleRegenerateEmbedding(params.row.id)}
            title={t('ai:faq.actions.regenerateEmbedding')}
          >
            <AutoFixHigh fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row.id)}
            title={t('ai:faq.actions.delete')}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color="#DC143C" sx={{ mb: 1 }}>
            {t('ai:faq.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('ai:faq.description')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AutoFixHigh />}
            onClick={handleBulkRegenerate}
            disabled={loading}
          >
            {t('ai:faq.actions.regenerateAllEmbeddings')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B0121A' } }}
          >
            {t('ai:faq.actions.add')}
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label={t('ai:faq.filters.search')}
                variant="outlined"
                size="small"
                fullWidth
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                placeholder={t('ai:faq.filters.search')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('ai:faq.filters.category')}</InputLabel>
                <Select
                  value={filters.category}
                  label={t('ai:faq.filters.category')}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                >
                  <MenuItem value="all">{t('ai:faq.filters.allCategories')}</MenuItem>
                  {categoryOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('ai:faq.filters.status')}</InputLabel>
                <Select
                  value={filters.is_active}
                  label={t('ai:faq.filters.status')}
                  onChange={(e) => setFilters({ ...filters, is_active: e.target.value })}
                >
                  <MenuItem value="all">{t('ai:faq.filters.all')}</MenuItem>
                  <MenuItem value="true">{t('ai:faq.filters.active')}</MenuItem>
                  <MenuItem value="false">{t('ai:faq.filters.inactive')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', height: '100%' }}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchFAQsList}
                  disabled={loading}
                >
                  {t('ai:faq.actions.refresh')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* DataGrid */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={addSerialNumbers(faqs, page, pageSize)}
              columns={columns}
              loading={loading}
              paginationMode="server"
              rowCount={totalCount}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setPage(0);
              }}
              pageSizeOptions={[10, 20, 50, 100]}
              disableSelectionOnClick
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: '#DC143C',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  padding: { xs: '8px 4px', sm: '8px 12px' }
                },
                '& .MuiDataGrid-columnSeparator': {
                  color: 'white'
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingFAQ ? t('ai:faq.dialog.edit') : t('ai:faq.dialog.create')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label={`${t('ai:faq.form.question')} *`}
              fullWidth
              multiline
              rows={2}
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              placeholder={t('ai:faq.form.questionPlaceholder')}
            />
            <TextField
              label={`${t('ai:faq.form.answer')} *`}
              fullWidth
              multiline
              rows={6}
              value={formData.answer}
              onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
              placeholder={t('ai:faq.form.answerPlaceholder')}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('ai:faq.form.category')}</InputLabel>
                  <Select
                    value={formData.category}
                    label={t('ai:faq.form.category')}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {categoryOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('ai:faq.form.priority')}
                  type="number"
                  fullWidth
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                  helperText={t('ai:faq.form.priorityHelper')}
                />
              </Grid>
            </Grid>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={formData.keywords}
              onChange={(event, newValue) => {
                setFormData({ ...formData, keywords: newValue });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('ai:faq.form.keywords')}
                  placeholder={t('ai:faq.form.keywordsPlaceholder')}
                  helperText={t('ai:faq.form.keywordsHelper')}
                />
              )}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label={t('ai:faq.form.isActive')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('ai:faq.dialog.cancel')}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B0121A' } }}
          >
            {editingFAQ ? t('ai:faq.dialog.updateButton') : t('ai:faq.dialog.createButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FAQManagementPage;


